/**
 * 風險等級 —— 全站單一對照表（票 21／35，ADR-0007）。
 *
 * ## 為什麼要有這個檔案
 *
 * 風險等級在這個系統裡是**雙向的查表鍵**，而兩個方向長得不一樣：
 *
 * | 方向 | 形式 | 出處 |
 * |---|---|---|
 * | 送出去（篩選） | **數字** `4嚴重／3高／2中／1低` | `GetAll` 的 `riskLevels` 查詢參數 |
 * | 讀回來（顯示） | **字串** `"嚴重"` | `eos_risk_level`／`eol_risk_level`／`cve_risk_level`／`kev_risk_level` |
 *
 * 兩個方向各寫一次，就會有一邊寫錯 —— 而寫錯的表現是「篩選按下去沒東西」
 * 或「整排 tag 顏色全錯」，**兩種都不會報錯**。這是 ADR-0004 講的值語意地雷，
 * 與側欄 `system_name`、RBAC `module_key` 同型。
 *
 * ## 開這一份之前的狀況
 *
 * `LEVEL_VARIANT`（等級 → tag 顏色）在 `Assets/riskFilter.js`、
 * `Dashboard/HardwareRisk.js`、`Dashboard/SoftwareRisk.js` **各寫一次**，
 * 三份內容完全相同；等級清單又在 `helpers/vulnContract.js` 另有一份
 * （`VULN_LEVELS` + `SEVERITY_ALIASES`）。ADR-0007 的裁示是「只能有一份」，
 * 所以那些地方現在都引這裡。
 *
 * CVE 嚴重度與 EOS／EOL 風險等級是不同的**概念**（一個是漏洞多嚴重，
 * 一個是還剩幾天到期），但值域與色階是同一套 —— 分成兩份表遲早對不起來。
 *
 * ## 「依剩餘天數換算」的規則不在這裡
 *
 * 後端算 `*_risk_level` 的門檻（幾天算嚴重）**沒有寫在 swagger 裡**，
 * 前端也不需要知道就能顯示。ADR-0007 的裁示是前端不自己再算一套 ——
 * 這個檔案只負責「後端給的值 ↔ 前端的四級」，不含任何日期運算。
 */

/** 四個等級，由重到輕。畫面上的 chip 順序、彙總的欄位順序都靠它 */
export const RISK_LEVELS = ["嚴重", "高", "中", "低"];

/** 等級 → `StatusTag` 的變體（列表、看板、篩選面板共用同一份色階） */
export const RISK_LEVEL_VARIANT = {
  嚴重: "redsolid",
  高: "error",
  中: "warning",
  低: "gary",
};

/** 等級 → 查詢參數的數字（`riskLevels` 是 int[]） */
const LEVEL_TO_CODE = { 嚴重: 4, 高: 3, 中: 2, 低: 1 };

/**
 * 認得的寫法 → 中文等級。key 一律以小寫比對。
 *
 * 數字也收：查詢參數用的就是 4/3/2/1，後端哪天在回應裡也改用數字
 * （或數字字串）時，不該讓整欄變成讀不懂的「4」。
 * **0 刻意不在表裡** —— 0 不是等級，收了它等於把「沒有值」變成「有值」。
 */
const RISK_LEVEL_ALIASES = {
  critical: "嚴重",
  high: "高",
  medium: "中",
  moderate: "中",
  low: "低",
  4: "嚴重",
  3: "高",
  2: "中",
  1: "低",
  嚴重: "嚴重",
  高: "高",
  中: "中",
  低: "低",
};

/**
 * 「沒有等級」的寫法。2026-09-07 smoke:`vulnerability_hits[].severity` 的實際值域是
 * 嚴重／高／中／**無** —— 「無」是「這筆沒有等級」,不是一個沒見過的等級,
 * 不該吵、也不該留成一顆寫著「無」的 tag。與 0 一樣,對外一律回空字串。
 * 只收實測見過的寫法(ADR-0004:抄實測,不猜);後端哪天回 none 之類的再補。
 */
// "0":Dashboard 明細的 severity_id 對 severity「無」給 0(2026-09-08 fixture)。0 不是等級,同「無」處理。
const NO_LEVEL = new Set(["無", "0"]);

const warned = new Set();

/**
 * 後端回的值 → 前端的四級。
 *
 * 對不上時**保留原值**並吵一聲：寧可畫面上出現一個沒見過的字（看得到、查得出來），
 * 也不要靜靜變成空白（沒有人會發現）。
 */
export function normalizeRiskLevel(raw) {
  const key = String(raw == null ? "" : raw).trim();
  if (!key) return "";
  if (NO_LEVEL.has(key.toLowerCase())) return "";
  const hit = RISK_LEVEL_ALIASES[key.toLowerCase()] || RISK_LEVEL_ALIASES[key];
  if (hit) return hit;
  if (!warned.has(key)) {
    warned.add(key);
    console.warn(
      `[riskLevel] 風險等級「${key}」不在對照表裡 —— 它會被當成 StatusTag 的查表鍵,`,
      `對不上就是安靜地變成預設樣式(顏色全錯但沒人發現);篩選也送不出對應的數字。`,
      `目前認得:${RISK_LEVELS.join("／")}(含 critical/high/medium/low 與 4/3/2/1 變體)。`,
      "請補進 helpers/riskLevel.js 的 RISK_LEVEL_ALIASES。"
    );
  }
  return key;
}

/**
 * 前端等級 → 查詢參數的數字。
 *
 * 認不得回 **null 不回 0**：0 會被 axios 當成有效參數送出去，
 * 變成一個「查不到任何東西」的條件，而畫面上看起來只是「這個條件沒資料」。
 */
export function riskLevelToCode(level) {
  const normalized = normalizeRiskLevel(level);
  return LEVEL_TO_CODE[normalized] ?? null;
}

/** 整批轉換（`riskLevels` 查詢參數）；認不得的直接丟掉，不留 null 在陣列裡 */
export function riskLevelsToCodes(levels) {
  return (levels || []).map(riskLevelToCode).filter((c) => c !== null);
}
